/**
 * The main menu: its entries, its Thai sections, and what one person sees (packages/policy/src/nav.ts).
 *
 *   ปักหมุด            the person's own favourites, in their order (always shown, even an entry the team hid)
 *   พื้นที่ทำงาน         everyday work
 *   งานขายและเอกสาร     customers, projects, catalog, TOR, สารบรรณ, document links
 *   เครื่องมือ           photo and PDF tools
 *   การสื่อสาร          chat, community, activity
 *
 * An entry the workspace's OWNER/ADMIN hid (`navHidden`) leaves every section; its page still opens
 * from a link, a search (Ctrl+K) or a pin. The phone's bottom bar is หน้าแรก plus three: the person's
 * pins first, then งานนำเสนอ, แชท, กิจกรรม (whatever of them is not hidden); the rest is in "เมนู".
 * Lives in the app shell: no data request, no extra chunk.
 */
import type { NavEntry } from '@xcreative/policy';
import Bell from 'lucide-react/dist/esm/icons/bell.mjs'; import Briefcase from 'lucide-react/dist/esm/icons/briefcase.mjs'; import ClipboardCheck from 'lucide-react/dist/esm/icons/clipboard-check.mjs'; import Contact from 'lucide-react/dist/esm/icons/contact.mjs'; import FileCheck2 from 'lucide-react/dist/esm/icons/file-check-corner.mjs'; import FileCog from 'lucide-react/dist/esm/icons/file-cog.mjs'; import FileType2 from 'lucide-react/dist/esm/icons/file-type-corner.mjs'; import FolderOpen from 'lucide-react/dist/esm/icons/folder-open.mjs'; import Hash from 'lucide-react/dist/esm/icons/hash.mjs'; import House from 'lucide-react/dist/esm/icons/house.mjs'; import ImagePlus from 'lucide-react/dist/esm/icons/image-plus.mjs'; import LayoutTemplate from 'lucide-react/dist/esm/icons/layout-template.mjs'; import ListChecks from 'lucide-react/dist/esm/icons/list-checks.mjs'; import MessageCircle from 'lucide-react/dist/esm/icons/message-circle.mjs'; import Package from 'lucide-react/dist/esm/icons/package.mjs'; import Presentation from 'lucide-react/dist/esm/icons/presentation.mjs'; import ScanLine from 'lucide-react/dist/esm/icons/scan-line.mjs'; import Stamp from 'lucide-react/dist/esm/icons/stamp.mjs';

export type NavIcon = typeof House;
export type NavSectionId = 'work' | 'sales' | 'tools' | 'comms';

export interface NavItem {
  slug: NavEntry;
  path: string;
  label: string;
  /** The phone's bottom bar (a few characters wide), when shorter than `label`. */
  tab?: string;
  icon: NavIcon;
  section: NavSectionId;
  /** Shown only in a workspace where that optional module is on (WorkspaceSummary.modules; the server enforces it too). */
  module?: string;
}

export const NAV_SECTIONS: { id: NavSectionId; label: string }[] = [
  { id: 'work', label: 'พื้นที่ทำงาน' },
  { id: 'sales', label: 'งานขายและเอกสาร' },
  { id: 'tools', label: 'เครื่องมือ' },
  { id: 'comms', label: 'การสื่อสาร' },
];

export const NAV: NavItem[] = [
  { slug: 'home', path: '/', label: 'หน้าหลัก', tab: 'หน้าแรก', icon: House, section: 'work' },
  { slug: 'presentations', path: '/presentations', label: 'โปรเจกต์ของฉัน', tab: 'งานนำเสนอ', icon: Presentation, section: 'work' },
  { slug: 'tasks', path: '/tasks', label: 'งาน', icon: ListChecks, section: 'work' },
  { slug: 'templates', path: '/templates', label: 'เทมเพลต', icon: LayoutTemplate, section: 'work' },
  { slug: 'files', path: '/files', label: 'ไฟล์และโฟลเดอร์', tab: 'ไฟล์', icon: FolderOpen, section: 'work' },
  { slug: 'reviews', path: '/reviews', label: 'งานอนุมัติ', tab: 'อนุมัติ', icon: ClipboardCheck, section: 'work' },
  { slug: 'customers', path: '/customers', label: 'ลูกค้าและประเภทงาน', tab: 'ลูกค้า', icon: Contact, section: 'sales' },
  { slug: 'solutions', path: '/solutions', label: 'Solution Studio', tab: 'โครงการ', icon: Briefcase, section: 'sales', module: 'solutionStudio' },
  { slug: 'catalog', path: '/catalog', label: 'แค็ตตาล็อกกลาง', tab: 'แค็ตตาล็อก', icon: Package, section: 'sales' },
  { slug: 'tor', path: '/tor', label: 'TOR', icon: FileCheck2, section: 'sales' },
  { slug: 'letters', path: '/letters', label: 'สารบรรณ', icon: Stamp, section: 'sales' },
  { slug: 'doc-links', path: '/doc-links', label: 'ลิงก์เอกสาร', tab: 'ลิงก์', icon: FileCheck2, section: 'sales' },
  { slug: 'photo', path: '/photo', label: 'สตูดิโอภาพ', tab: 'ภาพ', icon: ImagePlus, section: 'tools' },
  { slug: 'convert', path: '/convert', label: 'แปลง PDF เป็น Word', tab: 'PDF→Word', icon: FileType2, section: 'tools' },
  { slug: 'pdf', path: '/pdf', label: 'เครื่องมือ PDF', tab: 'PDF', icon: FileCog, section: 'tools' },
  { slug: 'scan', path: '/scan', label: 'สแกนเอกสาร', tab: 'สแกน', icon: ScanLine, section: 'tools' },
  { slug: 'chat', path: '/chat', label: 'แชท', icon: MessageCircle, section: 'comms' },
  { slug: 'channels', path: '/channels', label: 'ชุมชน', icon: Hash, section: 'comms' },
  { slug: 'activity', path: '/activity', label: 'กิจกรรม', icon: Bell, section: 'comms' },
];

/** The phone's bottom bar after หน้าแรก, when the person pinned nothing. */
const DEFAULT_TABS: NavEntry[] = ['presentations', 'chat', 'activity'];

export interface MenuPrefs {
  modules?: string[];
  navHidden?: string[];
  navPins?: string[];
}

export interface MenuModel {
  /** Entries of this workspace (modules switched on), whether hidden or not — what can be pinned. */
  available: NavItem[];
  pins: NavItem[];
  sections: { id: NavSectionId; label: string; items: NavItem[] }[];
  /** The phone's bottom bar: หน้าแรก + three. */
  tabs: NavItem[];
  /** The phone's "เมนู" sheet: every shown entry that is not on the bottom bar. */
  more: NavItem[];
}

export function menuModel(ws: MenuPrefs | null | undefined): MenuModel {
  const modules = ws?.modules ?? [];
  const available = NAV.filter((n) => !n.module || modules.includes(n.module));
  const by = (slug: string) => available.find((n) => n.slug === slug);
  const pins = (ws?.navPins ?? []).map(by).filter((n): n is NavItem => !!n);
  const hidden = new Set(ws?.navHidden ?? []);
  const shown = (n: NavItem) => !hidden.has(n.slug) || pins.includes(n);
  const rest = available.filter((n) => !hidden.has(n.slug) && !pins.includes(n));
  const sections = NAV_SECTIONS.map((s) => ({ ...s, items: rest.filter((n) => n.section === s.id) })).filter((s) => s.items.length > 0);
  const home = by('home')!;
  const tabs: NavItem[] = [home];
  for (const n of [...pins, ...DEFAULT_TABS.map(by).filter((n): n is NavItem => !!n && shown(n))]) {
    if (tabs.length >= 4) break;
    if (!tabs.includes(n)) tabs.push(n);
  }
  const more = [...pins, ...rest].filter((n) => !tabs.includes(n));
  return { available, pins, sections, tabs, more };
}

/** "Ctrl" or "⌘" for the shortcut hints (the key listener accepts both everywhere). */
export const modKey = (): string => (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? '⌘' : 'Ctrl');

let customizer: (() => void) | null = null;
/** The app shell registers how to open "ปรับแต่งเมนู" while it is on screen. */
export function setMenuCustomizer(open: (() => void) | null): void {
  customizer = open;
}
/** Open "ปรับแต่งเมนู" when the app shell is on screen; false when it is not (e.g. in the editor). */
export function openMenuCustomizer(): boolean {
  if (!customizer) return false;
  customizer();
  return true;
}
